// @flow

import React, {useState} from "react";
import "react-medium-image-zoom/dist/styles.css";
import "./Gallery.scss";
import "./swiper.scss";
import {Navigation, Pagination} from "swiper/modules";
import {Swiper, SwiperSlide} from "swiper/react";
import {createPortal} from "react-dom";
import {useTranslation} from "react-i18next";
import i18n from "../../i18next";

export type SlImage = {
    url: string,
    name: string,
    attachmentUrl: string,
    previewImage: ?SlImage,
};

type GalleryProps = {
    images: SlImage[],
    videos: any[],
}

const Gallery = ({
                     images,
                     videos
                 }: GalleryProps) => {

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedImage, setSelectedImage] = useState(0);

    const {t} = useTranslation();

    const openModal = index => {
        setSelectedImage(index);
        setIsModalOpen(true);
        document.body.classList.add("modal-open");
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedImage(0);
        document.body.classList.remove("modal-open");
    };

    const hasMultipleGalleryItems = images.length + videos.length > 1;

    const renderMedia = () => {

        const imageList = images.map(it => ({
            url: it.attachmentUrl,
            name: it.name,
            caption: it.caption,
            attachmentUrl: it.attachmentUrl,
            previewImage: it.previewImage,
        }));

        return (
            <React.Fragment>
                <div className={"swiper"}>
                    <Swiper
                        modules={[Navigation, Pagination]}
                        className={"swiper-container swiper-gal"}
                        slidesPerView={1}
                        spaceBetween={0}
                        slidesPerGroup={1}
                        autoHeight={true}
                        loop={false}
                        grabCursor={true}
                        a11y={true}
                        observer={true}
                        observeParents={true}
                        resizeObserver={true}
                        onSwiper={(sw) => {
                            const safeUpdate = () => {
                                if (sw && !sw.destroyed && sw.params) sw.updateAutoHeight(0);
                            };
                            requestAnimationFrame(safeUpdate);
                            setTimeout(safeUpdate, 100);

                            const wrap = sw.wrapperEl;
                            if (window.__hpmGalHeightMO) window.__hpmGalHeightMO.disconnect();
                            setTimeout(() => {
                                if (!wrap || !wrap.isConnected) return;
                                let lastH = parseFloat(wrap.style.height) || wrap.getBoundingClientRect().height;
                                let anim = null;
                                const mo = new MutationObserver(() => {
                                    const newH = parseFloat(wrap.style.height);
                                    if (!newH || Math.abs(newH - lastH) < 1) return;
                                    if (anim) anim.cancel();
                                    anim = wrap.animate(
                                        [{height: lastH + "px"}, {height: newH + "px"}],
                                        {duration: 250, easing: "ease"}
                                    );
                                    lastH = newH;
                                });
                                window.__hpmGalHeightMO = mo;
                                mo.observe(wrap, {attributes: true, attributeFilter: ["style"]});
                            }, 250);
                        }}
                        pagination={{
                            renderBullet: function (index, className) {
                                const ariaLabel = t("productDetail.gallery.jumpToImageNumber", {number: index + 1});
                                return "<button type=\"button\" class=\"" + className + "\" aria-label=\"" + ariaLabel + "\" ></button>";
                            },
                            el: ".swiper-pagination",
                            clickable: true,
                        }}
                        navigation={{
                            nextEl: ".swiper-button-next-gal",
                            prevEl: ".swiper-button-prev-gal",
                        }}
                    >
                        {imageList.map((it, index) => (
                            <SwiperSlide className="swiper-slide" key={"image_" + index}>
                                {/* <img> in einen <button> verpackt: <img> selbst
                                    ist a11y-rechtlich non-interactive — der
                                    Klick-Handler gehört auf einen <button>.
                                    Das Bild wird über width:100% + height:auto
                                    in voller Container-Breite gerendert; die
                                    intrinsische Aspect-Ratio des <img>-Tags
                                    bestimmt die Höhe (Swiper autoHeight). */}
                                <button type="button"
                                        className="hpm-gallery-imageButton"
                                        onClick={() => openModal(index)}
                                        aria-label={it.caption || t("productDetail.gallery.jumpToImageNumber", {number: index + 1})}>
                                    <img src={it.attachmentUrl || it.previewImageUrl}
                                         alt={it.caption || ""}
                                         className="hpm-gallery-image"
                                         onLoad={(e) => {
                                             const swiperEl = e.currentTarget.closest(".swiper-gal");
                                             const sw = swiperEl && swiperEl.swiper;
                                             if (sw && !sw.destroyed && sw.params) {
                                                 sw.updateAutoHeight(0);
                                             }
                                         }}
                                    />
                                </button>
                            </SwiperSlide>
                        ))}
                        {videos.map((video, index) => (
                            <SwiperSlide key={"video_" + index}>
                                <video controls className={"video-slide"}
                                       onLoadedMetadata={(e) => {
                                           const swiperEl = e.currentTarget.closest(".swiper-gal");
                                           const sw = swiperEl && swiperEl.swiper;
                                           if (sw && !sw.destroyed && sw.params) {
                                               sw.updateAutoHeight(0);
                                           }
                                       }}>
                                    <source src={video.attachmentUrl} type="video/mp4"/>
                                    <track kind="captions" srcLang={i18n?.language ?? "de"} src={video.caption} label={i18n?.language ?? "de"}/>
                                    Your browser does not support the video tag.
                                </video>
                            </SwiperSlide>
                        ))}
                    </Swiper>
                    <div
                        className={`swiper-button-next swiper-button-next-gal ${!hasMultipleGalleryItems ? "gal-hidden" : ""}`}/>
                    <div
                        className={`swiper-button-prev swiper-button-prev-gal ${!hasMultipleGalleryItems ? "gal-hidden" : ""}`}/>
                </div>
                <div
                    className={`swiper-pagination swiper-pagination-gal hpm-gallery-swiper-pagination .swiper-pagination-bullet-active previewImages image ${!hasMultipleGalleryItems ? "gal-hidden" : ""}`}/>
                {imageList.map((it, index) => (
                    <style key={"teaser_style_" + index}>
                        {`.hpm-gallery-swiper-pagination .swiper-pagination-bullet:nth-child(${index + 1}) {`}
                        {`background-image: url('${it.previewImage ? it.previewImage.attachmentUrl : it.attachmentUrl}') !important;`}
                        {"}"}
                    </style>
                ))}
                {videos.map((video, index) => (
                    <style key={"teaser_style_video_" + index}>
                        {`.hpm-gallery-swiper-pagination .swiper-pagination-bullet:nth-child(${imageList.length + index + 1}) {`}
                        {"opacity: 0.5 !important;"}
                        {`background-image: url('${video.previewImage ? video.previewImage.attachmentUrl : video.attachmentUrl}') !important;`}
                        {"}"}
                        {`.hpm-gallery-swiper-pagination .swiper-pagination-bullet:nth-child(${imageList.length + index + 1}):after {`}
                        {"content: \"\";"}
                        {"}"}
                    </style>
                ))}
            </React.Fragment>
        );
    };

    return (
        <div className="hpm-gallery" data-testid={"gallery"}>
            <div className="image">
                {renderMedia()}
            </div>
            {isModalOpen && createPortal(
                <div className={"hpm-App"} style={{zIndex: 1000000}}>
                    <div
                        className="hpm-gallery-modal"
                        onClick={closeModal}
                        onKeyDown={e => {
                            if (e.key === "Enter" || e.key === " " || e.key === "Escape") {
                                closeModal();
                            }
                        }}
                        role="button"
                        tabIndex={0}
                    >
                        <div
                            className="hpm-gallery-modal-close"
                            onClick={closeModal}
                            onKeyDown={e => {
                                if (e.key === "Enter" || e.key === " ") {
                                    closeModal();
                                }
                            }}
                            role="button"
                            tabIndex={0}
                            aria-label="Close"
                        ></div>
                        <div
                            className="hpm-gallery-modal-content"
                            onClick={e => e.stopPropagation()}
                            role="presentation"
                        >
                            <Swiper
                                modules={[Navigation]}
                                className={"swiper-container swiper-modal"}
                                id={"itt-modal"}
                                slidesPerView={1}
                                spaceBetween={0}
                                slidesPerGroup={1}
                                loop={false}
                                grabCursor={true}
                                navigation={{
                                    nextEl: ".swiper-button-next-modal",
                                    prevEl: ".swiper-button-prev-modal",
                                }}
                                initialSlide={selectedImage}
                            >
                                {images.map((image, index) => (
                                    <SwiperSlide className="swiper-slide" key={"modal_image_" + index}>
                                        <img src={image.attachmentUrl} alt={image.caption} className="hpm-modal-image"/>
                                    </SwiperSlide>
                                ))}
                                {videos.map((video, index) => (
                                    <SwiperSlide className="swiper-slide" key={"modal_video_" + index}>
                                        <video controls className="hpm-modal-video">
                                            <source src={video.attachmentUrl} type="video/mp4"/>
                                            <track kind="captions" srcLang={i18n?.language ?? "de"} src={video.caption} label={i18n?.language ?? "de"}/>
                                            Your browser does not support the video tag.
                                        </video>
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                            <div className="swiper-button-next swiper-button-next-modal"/>
                            <div className="swiper-button-prev swiper-button-prev-modal"/>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default Gallery;
